import { useEffect, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Form, { Field as BaseField } from "rc-field-form";
import DateInput from "../../../../../components/Inputs/DateInput";
import ResourceSelect from "../../../../../components/Inputs/ResourceSelect";
import Field from "../../../../../components/Field";
import Button from "../../../../../components/Button";
import MoneyInput from "../../../../../components/Inputs/MoneyInput";
import CompensationSelect from "../../../../../components/Inputs/CompensationSelect";
import CurrencySelect from "../../../../../components/Inputs/CurrencySelect";
import { ProfileContext } from "../../../context";
import { generateErrorsConfigForForm } from "../../../../../utils/helpers/errors";
import { getPrimaryActiveResource } from "../../../../../utils/helpers/user";
import {
	getSettingsResourceSearchFilters,
	renderSettingsResourceLabel,
} from "../../../../../utils/helpers/settings";
import { useProfileApi } from "../../../../../utils/api/profile";
import { usePeopleApi } from "../../../../../utils/api/people";
import { renderCurrencySymbol } from "../../../../../utils/helpers/job";
import { useCompany } from "../../../../../utils/hooks/company";
import {
	applyTimezone,
	dateTimeParseZone,
} from "../../../../../utils/helpers/date";
import Switch from "../../../../../components/Inputs/Switch";
import { useIsFieldDisabled } from "../../../useIsFieldDisabled";

function ChangeJobInformation({ close }) {
	const { t } = useTranslation();
	const company = useCompany();

	const timezone = useMemo(
		() => company?.settings?.timezone?.value,
		[company?.settings?.timezone?.value],
	);

	const { user, submit, submitting, submittingError } =
		useContext(ProfileContext);
	const { changeJobInformation: profileSubmit } = useProfileApi();
	const { changeJobInformation: peopleSubmit } = usePeopleApi();

	const [form] = Form.useForm();
	const getIsFieldDisabled = useIsFieldDisabled("peopleApiConnection.jobInformation");

	const onFinish = useCallback(
		(values) => {
			const jobs = user?.jobs || [];
			const upcomingJob = jobs.find(
				({ status, primary }) => primary && status === "pending",
			);
			const job = upcomingJob || getPrimaryActiveResource(jobs);
			const data = {
				job: {
					id: job?.id,
					startDate: applyTimezone(values.startDate, timezone, true),
					jobId: values.job.id,
					currency: values.currency,
					hourlyOrSalaried: values.hourlyOrSalaried,
					salary: values?.salary || 0,
					hourlyRate: values?.hourlyRate || 0,
					otherRate1: values?.otherRate1 || 0,
					otherRate2: values?.otherRate2 || 0,
					otherRate3: values?.otherRate3 || 0,
					otherRate4: values?.otherRate4 || 0,
					primary: true,
				},
				isMultipleJob: values?.isMultipleJob
			};

			submit(profileSubmit, peopleSubmit, data, close);
		},
		[user?.jobs, timezone, close, submit, profileSubmit, peopleSubmit],
	);

	useEffect(() => {
		const jobs = user?.jobs || [];
		const upcomingJob = jobs.find(
			({ status, primary }) => primary && status === "pending",
		);
		const job = upcomingJob || getPrimaryActiveResource(jobs);
		const { jobId: id, code, description } = job || {};
		form.setFieldsValue({
			job: id ? { id, code, description } : undefined,
			startDate: job?.startDate
				? dateTimeParseZone(job?.startDate, "date")
				: undefined,
			hourlyOrSalaried: job?.hourlyOrSalaried,
			currency: job?.currency,
			hourlyRate: job?.hourlyRate,
			otherRate1: job?.otherRate1,
			otherRate2: job?.otherRate2,
			otherRate3: job?.otherRate3,
			otherRate4: job?.otherRate4,
			salary: (parseFloat(job?.salary || '0.00')).toFixed(2),
			isMultipleJob: user?.isMultipleJob || false,
			id: job?.id,
		});
	}, [form, user?.isMultipleJob, user?.jobs]);

	useEffect(() => {
		const fieldErrors = generateErrorsConfigForForm(
			[
				"startDate",
				"job",
				"hourlyOrSalaried",
				"currency",
				"hourlyRate",
				"otherRate1",
				"otherRate2",
				"otherRate3",
				"otherRate4",
				"salary",
			],
			submittingError,
		);
		form.setFields(fieldErrors);
	}, [form, submittingError]);

	return (
		<Form
			form={form}
			layout="vertical"
			onFinish={onFinish}
			scrollToFirstError
		>
			<Field
				name="startDate"
				label={t("effective-date")}
				rules={[
					{
						required: !getIsFieldDisabled("startDate"),
						message: t("required-effective-date"),
					},
				]}
				className="d-flex flex-column"
			>
				<DateInput
					placeholderText={t("effective-date")}
					disabled={getIsFieldDisabled("startDate")}
				/>
			</Field>

			<Field
				name="job"
				label={t("job")}
				rules={[
					{
						required: !getIsFieldDisabled("job"),
						message: t("required-job"),
					},
				]}
			>
				<ResourceSelect
					labelPropName="description"
					resourcePath="/jobs"
					renderLabel={renderSettingsResourceLabel}
					hasSearch
					getSearchFilters={getSettingsResourceSearchFilters}
					placeholder={t("job")}
					disabled={getIsFieldDisabled("job")}
				/>
			</Field>

			<Field
				name="hourlyOrSalaried"
				label={t("hourly-or-salary")}
				rules={[
					{
						required: !getIsFieldDisabled("hourlyOrSalaried"),
						message: t("required-hourly-or-salary"),
					},
				]}
			>
				<CompensationSelect
					placeholder={t("hourly-or-salary")}
					disabled={getIsFieldDisabled("hourlyOrSalaried")}
				/>
			</Field>

			<Field
				name="currency"
				label={t("pay-currency")}
				rules={[
					{
						required: !getIsFieldDisabled("currency"),
						message: t("required-pay-currency"),
					},
				]}
			>
				<CurrencySelect
					placeholder={t("currency")}
					disabled={getIsFieldDisabled("currency")}
				/>
			</Field>

			<BaseField shouldUpdate noStyle>
				{({ }, { }, { getFieldValue }) => {
					const compensation = getFieldValue("hourlyOrSalaried");

					switch (compensation) {
						case "H": {
							return (
								<>
									<Field
										name="hourlyRate"
										label={t("hourly-rate")}
										rules={[
											{
												required: !getIsFieldDisabled("hourlyRate"),
												message: t(
													"required-hourly-rate"
												),
											},
										]}
									>
										<MoneyInput
											prefix={renderCurrencySymbol(
												getFieldValue("currency"),
											)}
											className="form-control"
											placeholder={t("hourly-rate")}
											disabled={getIsFieldDisabled("hourlyRate")}
										/>
									</Field>
									<Field
										name="otherRate1"
										label={t("other-rate1")}
									>
										<MoneyInput
											prefix={renderCurrencySymbol(
												getFieldValue("currency"),
											)}
											className="form-control"
											placeholder={t("other-rate1")}
											disabled={getIsFieldDisabled("otherRate1")}
										/>
									</Field>
									<Field
										name="otherRate2"
										label={t("other-rate2")}
									>
										<MoneyInput
											prefix={renderCurrencySymbol(
												getFieldValue("currency"),
											)}
											className="form-control"
											placeholder={t("other-rate2")}
											disabled={getIsFieldDisabled("otherRate2")}
										/>
									</Field>
									<Field
										name="otherRate3"
										label={t("other-rate3")}
									>
										<MoneyInput
											prefix={renderCurrencySymbol(
												getFieldValue("currency"),
											)}
											className="form-control"
											placeholder={t("other-rate3")}
											disabled={getIsFieldDisabled("otherRate3")}
										/>
									</Field>
									<Field
										name="otherRate4"
										label={t("other-rate4")}
									>
										<MoneyInput
											prefix={renderCurrencySymbol(
												getFieldValue("currency"),
											)}
											className="form-control"
											placeholder={t("other-rate4")}
											disabled={getIsFieldDisabled("otherRate4")}
										/>
									</Field>
								</>
							);
						}
						default: {
							return (
								<Field
									name="salary"
									label={t("salary")}
									rules={[
										{
											required: !getIsFieldDisabled("salary"),
											message: t(
												"required-salary"
											),
										},
									]}
								>
									<MoneyInput
										prefix={renderCurrencySymbol(
											getFieldValue("currency"),
										)}
										className="form-control"
										disabled={getIsFieldDisabled("salary")}
									/>
								</Field>
							);
						}
					}
				}}
			</BaseField>

			<Field name="isMultipleJob" label={t("is-multiple-job")} valuePropName="checked">
				<Switch id="isMultipleJob" disabled={getIsFieldDisabled("isMultipleJob")} />
			</Field>

			<div className="d-flex justify-content-end">
				<Button
					className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
					onClick={close}
					disabled={submitting}
				>
					{t("cancel")}
				</Button>

				<Button
					className="btn-round btn-icon shadow-none border px-3 btn btn-dark btn-sm"
					color="primary"
					type="submit"
					loading={submitting}
				>
					{t("save")}
				</Button>
			</div>
		</Form>
	);
}

export default ChangeJobInformation;
